import { useState, useCallback, useEffect } from 'react';
import { adminApi } from '../api/admin';
import type { AdminUser, AdminShop, AdminStats } from '../api/admin';
import { getAdminToken, setAdminToken, clearAdminToken } from '../api/adminClient';

// ── Auth ──────────────────────────────────────────────────────────────────────

export function useAdminAuth() {
  const [admin, setAdmin]     = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);

  const loadMe = useCallback(async () => {
    if (!getAdminToken()) { setLoading(false); return; }
    try {
      const res = await adminApi.me();
      setAdmin(res.data.data);
    } catch {
      clearAdminToken();
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadMe(); }, [loadMe]);

  const login = useCallback(async (email: string, password: string) => {
    setError(null);
    setLoading(true);
    try {
      const res = await adminApi.login(email, password);
      setAdminToken(res.data.data.token);
      setAdmin(res.data.data.admin_user);
      return true;
    } catch (err: any) {
      setError(err?.response?.data?.message ?? 'Login failed.');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    clearAdminToken();
    setAdmin(null);
  }, []);

  return { admin, loading, error, login, logout };
}

// ── Stats ─────────────────────────────────────────────────────────────────────

export function useAdminStats() {
  const [stats, setStats]     = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminApi.stats();
      setStats(res.data.data);
    } catch {
      setError('Failed to load platform stats.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  return { stats, loading, error, refresh: fetch };
}

// ── Shops ─────────────────────────────────────────────────────────────────────

export function useAdminShops() {
  const [shops, setShops]     = useState<AdminShop[]>([]);
  const [loading, setLoading] = useState(false);
  const [actionId, setActionId] = useState<number | null>(null);
  const [error, setError]     = useState<string | null>(null);
  const [page, setPage]       = useState(1);
  const [search, setSearch]   = useState('');
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal]     = useState(0);

  const fetchShops = useCallback(async (p = 1, q = '') => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminApi.shops.list(p, q);
      setShops(res.data.data);
      setLastPage(res.data.meta.last_page);
      setTotal(res.data.meta.total);
    } catch {
      setError('Failed to load shops.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchShops(page, search); }, [fetchShops, page, search]);

  const suspend = useCallback(async (id: number) => {
    setActionId(id);
    try {
      await adminApi.shops.suspend(id);
      setShops((prev) => prev.map((s) => s.id === id ? { ...s, is_active: false } : s));
    } catch (err: any) {
      setError(err?.response?.data?.message ?? 'Failed to suspend shop.');
    } finally {
      setActionId(null);
    }
  }, []);

  const reactivate = useCallback(async (id: number) => {
    setActionId(id);
    try {
      await adminApi.shops.reactivate(id);
      setShops((prev) => prev.map((s) => s.id === id ? { ...s, is_active: true } : s));
    } catch (err: any) {
      setError(err?.response?.data?.message ?? 'Failed to reactivate shop.');
    } finally {
      setActionId(null);
    }
  }, []);

  return { shops, loading, error, actionId, page, lastPage, total, search, setPage, setSearch, suspend, reactivate };
}

// ── Admin Users ───────────────────────────────────────────────────────────────

export function useAdminUsers() {
  const [users, setUsers]     = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState<string | null>(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.users.list();
      setUsers(res.data.data);
    } catch {
      setError('Failed to load admin users.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const create = useCallback(async (data: { name: string; email: string; password: string; role: string }) => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminApi.users.create(data);
      setUsers((prev) => [...prev, res.data.data]);
      return res.data.data;
    } catch (err: any) {
      setError(err?.response?.data?.message ?? 'Failed to create admin user.');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const remove = useCallback(async (id: number) => {
    setLoading(true);
    try {
      await adminApi.users.delete(id);
      setUsers((prev) => prev.filter((u) => u.id !== id));
    } catch (err: any) {
      setError(err?.response?.data?.message ?? 'Failed to delete admin user.');
    } finally {
      setLoading(false);
    }
  }, []);

  return { users, loading, error, create, remove, refresh: fetchUsers };
}
